import type {
  NotifiableUserPreferences,
  NOTIFICATION_CHANNEL,
} from "@nlc/shared-types";

import { NOTIFICATION_CATEGORY } from "@nlc/shared-types";
import { PreferenceOptions, useSuprSendClient } from "@suprsend/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { notificationsKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

import { showToast, ToastTypeEnum } from "../settings/utils/showToast";
import { useUpdateCustomProperties } from "./useUpdateCustomProperties";

interface SetPreferenceInput {
  category: NOTIFICATION_CATEGORY;
  channel: NOTIFICATION_CHANNEL;
  enabled: boolean;
}

const SUPRSEND_CATEGORY_MAP: Partial<Record<NOTIFICATION_CATEGORY, string>> = {
  [NOTIFICATION_CATEGORY.COMMENT_ADDED]: "request-comment-attachment-is-added",
  [NOTIFICATION_CATEGORY.DUE_DATE]: "request-due-date",
  [NOTIFICATION_CATEGORY.FEEDBACK_TOOL]: "feedback-tool",
  [NOTIFICATION_CATEGORY.I_AM_MENTIONED]: "request-mentioned-or-tagged",
  [NOTIFICATION_CATEGORY.REQUEST_CREATED]: "request-created",
  [NOTIFICATION_CATEGORY.REQUEST_UPDATED]: "request-updated",
  [NOTIFICATION_CATEGORY.STATUS_UPDATED]: "request-status-change",
};

export function useSetNotificationPreference() {
  const queryClient = useQueryClient();
  const queryKey = notificationsKeys.settings();
  const suprSendClient = useSuprSendClient();
  const { mutateAsync: updateCustomProperties } = useUpdateCustomProperties();

  return useMutation({
    mutationFn: (input: SetPreferenceInput) =>
      apiClient.url(`/users/me/notification-settings`).patch(input).res(),

    onMutate: ({ category, channel, enabled }) => {
      const previousData =
        queryClient.getQueryData<NotifiableUserPreferences>(queryKey);

      queryClient.setQueryData<NotifiableUserPreferences>(queryKey, (old) => {
        if (!old) return old;

        const current = old.preferences[channel] ?? [];
        const next = enabled
          ? [...current, category]
          : current.filter((c) => c !== category);

        return {
          ...old,
          preferences: { ...old.preferences, [channel]: next },
        };
      });

      return { previousData };
    },
    // eslint-disable-next-line perfectionist/sort-objects
    onError: async (_err, _var, context) => {
      queryClient.invalidateQueries({ queryKey });

      queryClient.setQueryData<NotifiableUserPreferences>(
        queryKey,
        context?.previousData,
      );

      showToast(
        "Failed to update notification preferences",
        ToastTypeEnum.ERROR,
      );
    },

    // TODO: remove once the batch notifications service is fully rolled out.
    // Keeps SuprSend preferences in sync while the migration is in progress.
    onSuccess: async (_, { category, channel, enabled }) => {
      if (
        category ===
        NOTIFICATION_CATEGORY.UPDATES_FROM_REQUESTS_CREATED_BY_OTHERS
      ) {
        try {
          const user = await apiClient
            .url(`/notifications/supr-send/user`)
            .get()
            .json<{
              properties?: {
                blockChannels?: string | string[];
              };
            }>();

          const rawBlocked = user.properties?.blockChannels ?? [];

          const currentBlocked: string[] = Array.isArray(rawBlocked)
            ? rawBlocked
            : typeof rawBlocked === "string"
              ? JSON.parse(rawBlocked)
              : [];

          const updatedBlocked = enabled
            ? currentBlocked.filter((c: string) => c !== channel)
            : currentBlocked.includes(channel)
              ? currentBlocked
              : [...currentBlocked, channel];

          await updateCustomProperties({
            key: "blockChannels",
            value: JSON.stringify(updatedBlocked),
          });
        } catch {
          // Silently fail to not block user experience
        }

        return;
      }

      const categorySlug = SUPRSEND_CATEGORY_MAP[category];

      if (!categorySlug) return;

      suprSendClient.user.preferences
        .getPreferences()
        .then(() =>
          suprSendClient.user.preferences.updateChannelPreferenceInCategory(
            channel,
            enabled ? PreferenceOptions.OPT_IN : PreferenceOptions.OPT_OUT,
            categorySlug,
          ),
        )
        .catch(() => {});
    },
  });
}
